/*!
// <copyright file="NotificationHandling.ts" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

import { util } from "@ax/office-shared-lib";
import * as $ from "jquery";
import { resources } from "@ax/office-cdn-app-resources";
import { notifications } from "./Notifications";
import { telemetryLogging } from "./TelemetryLogging";
import { messageBoxHandling } from "./MessageBoxHandling";
import { messageCenter } from "./MessageCenter";

export namespace notificationHandling {

    /** Handles notifications for the app. */
    export class NotificationsHandler {

        /** The notifications div to interact with. */
        private notificationsDiv: JQuery;

        /** The notifications button. */
        private _notificationsButton: JQuery;

        /** The notifications tooltip. */
        private _notificationsTooltip: JQuery;

        /** The notification status element, used to alert screen readers to unread error notifications. */
        private _notificationsStatusElement: JQuery;

        /** The count of new notifications. */
        private _newNotificationCount: number;

        /** The handler for the message center. */
        private _messageCenterHandler: messageCenter.MessageCenterHandler;

        /** The IDs of controls relating to notifications required by the notifications handler. */
        private static ControlIDs = {

            /** ID of the notifications button. */
            button: "notificationsButton",

            /** ID of the notifications status element. */
            status: "notificationsStatus",

        };

        /**
         * @param notificationsDiv The notifications div to interact with.
         */
        constructor(notificationsDiv: JQuery) {
            /* jshint validthis: true */

            this._notificationsButtonClicked = this._notificationsButtonClicked.bind(this);
            this.notificationsDiv = notificationsDiv;
            this._newNotificationCount = 0;
        }

        /** Initializes the notifications handler. */
        public initialize(): void {
            this._notificationsButton = util.findElementById(NotificationsHandler.ControlIDs.button, this.notificationsDiv);
            this._notificationsButton.click(this._notificationsButtonClicked);
            util.customTooltip(this._notificationsButton[0], resources.dynamicsApp.notificationsButtonTooltip(), "tooltipTextBottom");
            util.changeAttribute(this._notificationsButton, "aria-label", resources.dynamicsApp.notificationsButtonTooltip());
            this._notificationsStatusElement = util.findElementById(NotificationsHandler.ControlIDs.status, this.notificationsDiv);
            this._messageCenterHandler = new messageCenter.MessageCenterHandler();
            this._messageCenterHandler.initialize();
        }

        /**
         * Adds a notification to the handler.
         * @param notification The notification to add.
         * @param options The notification options.
         */
        public addNotification(notification: notifications.Notification, options?: notifications.AddNotificationOptions): void {
            if (notification.NotificationType === notifications.NotificationType.error) {
                const newCount = ++this._newNotificationCount;
                this._updateNotificationsButtonCount();
                window.setTimeout(
                    () => {
                        if (newCount === this._newNotificationCount) {
                            this._notificationsStatusElement.text(resources.dynamicsApp.messageCenterNotificationStatusFormat(newCount));
                        }
                    },
                    1000);
            }
            this._messageCenterHandler.addNotification(notification, options);
        }

        /**
         * Sets the help and support link in the message handler.
         * @param url The help and support url.
         */
        public setLearnMoreLink(url: string): void {
            this,this._messageCenterHandler.setLearnMoreLink(url);
        }

        /** Updates the notifications button's count text based on internal new count. */
        public _updateNotificationsButtonCount(): void {
            let buttonText = "";
            let label: string;
            if (this._newNotificationCount > 0) {
                buttonText = this._newNotificationCount.toString();
                label = resources.dynamicsApp.messageCenterNotificationStatusFormat(this._newNotificationCount);
            }
            this._notificationsButton.text(buttonText);
            util.changeAttribute(this._notificationsButton, util.AriaAttributes.Label, label);
        }

        /**
         * Event handler for when the notifications button is clicked which resets the new notifications count to 0.
         * @param event The notifications button clicked event.
         */
        public _notificationsButtonClicked(event?: JQuery.ClickEvent<HTMLElement>): void {
            if (this._newNotificationCount > 0) {
                this._messageCenterHandler.showErrorsOnly();
                this._newNotificationCount = 0;
                this._updateNotificationsButtonCount();
            }
            this._messageCenterHandler.openDialog();
        }
    }

    /** The handler for notifications. */
    export let handler: notificationHandling.NotificationsHandler;

    export interface ErrorOptions extends notifications.AddNotificationOptions {

        /** The error details to display and add. */
        Details?: string;
    }

    export interface ShowErrorOptions extends ErrorOptions {

        /** Indicates if the correlation ID should be displayed; false by default. */
        IncludeCorrelationID?: boolean;

        /** Includes telemetry properties that should be logged. */
        TelemetryProperties?: any;
    }

    /**
     * Displays an error to the user and adds an error to the message center.
     * @param message The error message to display and add.
     * @param errorCode The code that corresponds to the error.
     * @param options The error options to display.
     */
    export function showError(message: string, errorCode: string, options: ShowErrorOptions = {}): void {
        const logErrorOptions = $.extend(true, {}, options) as telemetryLogging.LogErrorTelemetryOptions;
        logErrorOptions.Severity = util.telemetry.ErrorSeverity.High;
        telemetryLogging.logErrorTelemetry(message, errorCode, logErrorOptions);
        messageBoxHandling.handler.showError(message, options);
        notificationHandling.handler.addNotification(new notifications.Notification(notifications.NotificationType.error, message, options.Details), { IncludeTimeStamp: options.IncludeTimeStamp });
    }

    /**
     * Displays a confirmation dialog to the user and prompts for an "Yes"/"No" answer without adding to the message center.
     * @param message The message to display.
     * @param callback The function to call with the user's response to the dialog.
     * @param options The options to adjust the showing of the confirmation dialog.
     */
    export function showConfirmationDialog(message: string, callback: messageBoxHandling.ShowConfirmationDialogCallback, options: messageBoxHandling.ShowConfirmationDialogOptions = {}): void {
        messageBoxHandling.handler.showConfirmationDialog(message, callback, options);
    }

    export interface ShowNotificationOptions {

        /** The error details to display and add. */
        Details?: string,

        /** Indicates whether a time stamp should be displayed with the notification; true by default. */
        IncludeTimeStamp?: boolean,

        /** Contains text and action properties that indicate how the option link should be displayed. */
        LinkOptions?: notifications.LinkOptions
    }

    /**
     * Displays a notification to the user and adds a notification to the message center.
     * @param message The notification message to display and add.
     * @param options The notification options to display.
     */
    export function showNotification(message: string, options?: ShowNotificationOptions): void {
        options = options || {};
        messageBoxHandling.handler.showNotification(message, options);
        notificationHandling.handler.addNotification(new notifications.Notification(notifications.NotificationType.notification, message, options.Details), { IncludeTimeStamp: options.IncludeTimeStamp });
    }

    /**
     * Adds a notification to the message center.
     * @param message The notification message to add.
     * @param includeTimeStamp Indicates whether a time stamp should be displayed with the notification; defaults to true.
     */
    export function addNotification(message: string, includeTimeStamp?: boolean): void {
        notificationHandling.handler.addNotification(new notifications.Notification(notifications.NotificationType.notification, message), { IncludeTimeStamp: includeTimeStamp });
    }

    export interface AddErrorNotificationOptions {

        /** The error details to display and add. */
        Details?: string,

        /** Indicates whether a time stamp should be displayed with the notification; true by default. */
        IncludeTimeStamp?: boolean,

        /** Indicates if telemetry should be logged for the error; false by default. */
        LogTelemetry?: boolean,

        /** Includes telemetry details that should be logged. */
        TelemetryDetails?: any,

        /** Includes telemetry properties that should be logged. */
        TelemetryProperties?: any,

        /** Contains text and action properties that indicate how the option link should be displayed. */
        LinkOptions?: notifications.LinkOptions
    }

    /**
     * Adds an error notification to the message center without displaying an error popup.
     * @param message The error message to add.
     * @param errorCode The code that corresponds to the error.
     * @param options The error options to display.
     */
    export function addErrorNotification(message: string, errorCode: string, options: AddErrorNotificationOptions = {}): void {
        notificationHandling.handler.addNotification(new notifications.Notification(notifications.NotificationType.error, message, options.Details), options);
        if (options.LogTelemetry === true) {
            const logErrorOptions = $.extend(true, {}, options) as telemetryLogging.LogErrorTelemetryOptions;
            logErrorOptions.Severity = util.telemetry.ErrorSeverity.Low;
            telemetryLogging.logErrorTelemetry(message, errorCode, logErrorOptions);
        }
    }
}
